import { z } from 'zod';
import { anomalyBuildingSchema } from '../inspection/anomaly-building.js';
import { anomalyEffectSchema } from '../inspection/anomaly-effect.js';
import { anomalyLocationSchema } from '../inspection/anomaly-location.js';
import { performedOnSchema } from '../planning/inspection-date.js';
import { inspectionResultSchema } from '../inspection/result.js';
import { severitySchema } from '../inspection/vocabulary.js';
import { SURVEY_V1_VERSION, surveyV1Schema } from '../inspection/survey-v1.js';
import { SURVEY_VERSION, surveyV2Schema, upgradeSurvey } from '../inspection/survey-v2.js';
import { uuidV7Schema } from '../uuid.js';
import { freeInspectionInputSchema } from './free-inspection.js';
/**
 * M19 — une anomalie telle que le terrain la remonte. En S4 elle est **composée à la main** :
 * aucune règle ne la génère encore, d'où l'absence de `ruleId`.
 *
 * Les cinq codes de vocabulaire voyagent **en texte, sans clé étrangère** : ils sont gelés à la
 * saisie. Une tablette a pu rater la dernière descente du référentiel, ou le back-office faire
 * du ménage entre le téléchargement et la remontée — refuser détruirait un relevé de terrain
 * (M17, M21). La `sentence`, elle, est exigée non vide : c'est l'archive, et la seule chose que
 * le rapport imprime.
 *
 * `location` — l'étage numéroté du test terrain du 25/08 — **ne se saisit plus depuis #114** :
 * la provenance passe par `provenance`, la colonne du constructeur. Le champ reste accepté, tel
 * quel, pour le brouillon d'une tablette d'avant qui le porte encore (D4) : le refuser ferait
 * tomber le relevé entier.
 */
export const submittedAnomalySchema = z.looseObject({
    sentence: z.string().min(1),
    severity: severitySchema,
    origin: z.string().nullish(),
    /**
     * **Le point d'eau désigné** dans la grille du relevé (#312) : son code (liste `fixture`), son
     * niveau (`fixture_location`) et son numéro parmi ceux que la case compte — « WC n°2 du 1ᵉʳ
     * étage ». Le compte, lui, reste à la grille : rien ne le double ici. Tout est `nullish` : une
     * origine qui n'est pas un point d'eau n'en désigne aucun, et une fiche d'avant #312 non plus.
     */
    fixture: z.string().nullish(),
    fixtureLocation: z.string().nullish(),
    fixtureIndex: z.number().int().min(1).nullish(),
    provenance: z.string().nullish(),
    evacuation: z.string().nullish(),
    destination: z.string().nullish(),
    /**
     * Le texte écrit sous « Autres » (#351) — une destination hors référentiel, pour ce contrôle.
     * `nullish` : une anomalie qui n'est pas « Autres » n'en porte pas. Un « Autres » laissé vide
     * arrive en `''` et s'enregistre — sa phrase dit « … », le bureau tranche (M21).
     */
    otherDestination: z.string().nullish(),
    /** Le texte de « Autres » des quatre autres colonnes (#583), sur la règle d'`otherDestination` (M45). */
    otherOrigin: z.string().nullish(),
    otherProvenance: z.string().nullish(),
    otherEvacuation: z.string().nullish(),
    otherTechnical: z.string().nullish(),
    technical: z.string().nullish(),
    /**
     * Ce que le client du contrôle fait de cette situation, **figé au relevé** (#413) — la tablette
     * le recopie de la tournée. Un effet que ce serveur ne connaît pas se lit comme aucun :
     * l'anomalie compte, et le relevé n'est pas refusé pour autant (M21).
     */
    effect: anomalyEffectSchema.nullish().catch(null),
    /** La phrase de préconisation du client pour cette situation, imprimée au rapport. */
    effectRecommendation: z.string().nullish(),
    /**
     * La question que la règle du client a posée à l'agent (« Couverte, avec bordures ? ») et sa
     * réponse, figées avec l'effet qu'elles ont décidé (#413). `null` : sans réponse.
     */
    effectCondition: z.string().nullish(),
    conditionMet: z.boolean().nullish(),
    location: anomalyLocationSchema.nullish(),
    /**
     * Quel immeuble de la parcelle — Maison, AR, AV (`anomalyBuildingSchema`).
     *
     * `nullish` pour deux raisons qui se cumulent : une parcelle à bâtiment unique n'a rien à
     * cocher, et un brouillon ouvert avant cette colonne n'en porte pas. Refuser transformerait
     * une fiche déjà saisie en relevé perdu (M21).
     */
    building: anomalyBuildingSchema.nullish(),
    manual: z.boolean(),
});
/**
 * D5 — le média **voyage à part** (PUT binaire vers le stockage objet) ; la soumission n'en
 * porte que la référence.
 *
 * L'`id` est **aussi la clé de l'objet stocké**, et il est généré côté client (M7) : c'est ce
 * qui rend le rejeu du PUT idempotent par construction, sans registre à tenir.
 */
export const submittedMediumSchema = z.looseObject({
    id: uuidV7Schema,
    /**
     * ⚠️ **Union fermée, donc à élargir avec chaque nouvelle famille.** Un type que ce schéma
     * ignore fait refuser l'enveloppe entière, sans qu'aucun écran de la tablette ne le voie
     * venir : la fiche part, le serveur la rejette, et l'agent lit un refus qui ne nomme pas son
     * dessin. `schematic` — l'image rendue du schéma réseau (M11) — est entrée par là, puis
     * `schematic_photo`, la photo d'un schéma fait sur papier (#397).
     */
    type: z.enum(['view', 'manhole', 'stormwater', 'signature', 'schematic', 'schematic_photo']),
    takenAt: z.iso.datetime({ offset: true }).nullish(),
    label: z.string().nullish(),
    /**
     * L'empreinte du dessin que l'image **montre** — seulement pour un média `schematic` (M11).
     *
     * ⚠️ **Sans elle, le bureau ne peut pas savoir si l'archive est à jour**, et c'est tout son
     * objet. Elle était restée locale à la tablette, où `hasOutdatedSchematicImage` la compare au
     * relevé courant ; le bureau, lui, la recevait toujours absente — et cette règle déclare
     * périmée toute image sans empreinte. Résultat : l'avertissement s'affichait sur **tous** les
     * contrôles, y compris ceux dont l'image montrait exactement le dessin transmis.
     *
     * `nullish` pour la raison de `performedOn` : un brouillon ouvert avant ce champ, ou une
     * archive d'avant la fonctionnalité, n'en porte pas — et le défaut « périmée » reste alors le
     * bon (au pire un export de trop, jamais une archive amputée en silence).
     */
    sourceFingerprint: z.string().nullish(),
    /**
     * Le type du fichier, tel que la tablette l'a rangé — `video/mp4`, `image/jpeg` (#418). Le
     * bureau sait ainsi qu'une pièce est une vidéo, à lire à la demande, sans en télécharger les
     * octets à l'ouverture du contrôle.
     *
     * `nullish` : une fiche d'avant ce champ n'en porte pas, et le bureau charge alors la pièce
     * pour en connaître le type, comme avant.
     */
    contentType: z.string().max(100).nullish(),
    /**
     * La pièce de la visite précédente que celle-ci **recopie** (#675) : l'identifiant du média
     * d'origine. Une contre-visite reprend les photos et pièces du contrôle qu'elle suit, dupliquées
     * dans le stockage sous un identifiant à elle — celui-ci n'est qu'une provenance, jamais une
     * clé. C'est ce qui garde distinct, au bureau, au rapport et à l'export, ce qui vient de la
     * première visite. `nullish` : une pièce prise sur place n'en porte pas.
     */
    carriedFrom: uuidV7Schema.nullish(),
});
/** Les pièces que la tablette produit elle-même — jamais comptées comme photos. */
const GENERATED_MEDIA_TYPES = ['signature', 'schematic'];
/**
 * Le nombre de **photos** d'un relevé — ce que la colonne « Photos » de la file de validation
 * affiche, et ce que `Inspection.photo_count` garde une fois le contrôle validé (M20).
 *
 * ⚠️ **Ni la signature ni le schéma réseau ne sont des photos** : ce sont des pièces que la
 * tablette génère, pas des clichés de l'agent. Son badge, son récapitulatif et l'avertissement
 * « Aucune photo jointe » les écartent, comme le récapitulatif de la fiche du bureau : la file et
 * `photo_count` annonçaient une photo de plus que ce que l'agent et le valideur voyaient.
 *
 * ⚠️ Écrite ici plutôt qu'à chaque appelant : la matérialisation en portait la seule copie, et
 * une seconde aurait divergé au premier type de média ajouté — exactement ce qui est arrivé à
 * `schematic`.
 */
export function proposalPhotoCount(payload) {
    return payload.media.filter((medium) => !GENERATED_MEDIA_TYPES.includes(medium.type)).length;
}
/**
 * Ce qu'un contrôle porte au grand maximum. Une vingtaine de photos et une dizaine d'anomalies
 * font une fiche chargée ; ces plafonds sont trois à cinq fois au-dessus.
 *
 * ⚠️ Ils bornent l'**amplification**, pas le métier : chaque média référencé coûte un
 * `HeadObject` au dépôt, sur le même client S3 que les envois des autres équipes. Sans plafond,
 * 1 Mo de corps tient ~10 000 identifiants, donc autant de requêtes simultanées. Le refus tombe
 * au schéma — donc dans un `400` qui **nomme le champ** (M21) — et pas au fond du service.
 */
export const MAX_MEDIA_PER_INSPECTION = 100;
export const MAX_ANOMALIES_PER_INSPECTION = 50;
/**
 * D4 / M16 / M20 — ce qu'une proposition terrain porte **en dehors du relevé**, commun aux deux
 * versions de celui-ci. Épelé une fois : deux listes de champs à tenir divergeraient au premier
 * ajout.
 *
 * `metPerson.capacity` est l'« en qualité de » de la fiche papier : réclamée par un
 * avertissement, jamais bloquante (M21). `lat`/`lon` sont le lieu de la **signature**, pas celui
 * du branchement (M20).
 */
const proposalFields = {
    anomalies: z.array(submittedAnomalySchema).max(MAX_ANOMALIES_PER_INSPECTION),
    /**
     * La conclusion que l'agent a **posée à la main**, quand il en a posé une (M19) — `null` ou
     * absente sinon, et c'est alors la dérivation qui décide (`resolveInspectionResult`).
     *
     * ⚠️ **Ne jamais y recopier la conclusion dérivée « pour avoir la valeur »** : l'absence est
     * l'information. Une fiche qui part avec la dérivation du moment de sa clôture fige un
     * constat que le relevé peut encore démentir — une anomalie retirée à la validation, une
     * règle de M19 corrigée l'an prochain — et plus rien ne distinguerait le choix de l'agent du
     * calcul de la machine. C'est précisément ce que la surcharge sert à dire.
     *
     * ⚠️ `nullish`, et **pas une v2 de la charge utile** : le contrat est un `looseObject` et une
     * tablette d'avant ce champ transmet une fiche sans lui. La refuser détruirait un relevé que
     * personne ne peut ressaisir (M21) — même régime que `performedOn` ci-dessous.
     */
    result: inspectionResultSchema.nullish(),
    media: z.array(submittedMediumSchema).max(MAX_MEDIA_PER_INSPECTION),
    metPerson: z
        .looseObject({
        lastName: z.string().nullish(),
        firstName: z.string().nullish(),
        /**
         * Son numéro, tel que l'agent le note — **jamais validé** (M21, M15) : ce qu'on relève sur
         * un pas de porte est parfois un portable, parfois un fixe d'agence, parfois « voir le
         * gardien ». Un format refusé ferait perdre le seul moyen de rappeler.
         */
        phone: z.string().nullish(),
        capacity: z.string().nullish(),
    })
        .nullish(),
    certifiedWaterPoints: z.boolean(),
    certifiedNoSepticTank: z.boolean(),
    /**
     * La case de consentement de Transmettre (#354, M37) — **facultative pour signer**.
     *
     * `nullish`, et l'absence est l'information : une tablette d'avant la case transmet une fiche
     * sans elle, et la refuser détruirait un relevé que personne ne peut ressaisir (M21). Le
     * serveur garde alors `null` — « non demandé » —, que rien ne doit confondre avec `false`,
     * « demandé, non coché ».
     */
    consentedDataRetention: z.boolean().nullish(),
    signatureMediaId: uuidV7Schema.nullish(),
    signedAt: z.iso.datetime({ offset: true }).nullish(),
    /**
     * **Le jour où le contrôle a réellement eu lieu** (#115), qui n'est pas celui du rendez-vous
     * et pas davantage celui de la clôture sur la tablette : une fiche se clôt le soir, parfois le
     * lendemain, et `submittedAt` ne date que ce geste-là.
     *
     * ⚠️ `nullish`, comme tout champ neuf de cette v1 (M21) : une tablette dont le bundle précède
     * ce champ transmet une fiche sans lui, et la refuser détruirait un relevé que personne ne peut
     * ressaisir. L'écran, lui, réclame la date — sans jamais bloquer la transmission.
     */
    performedOn: performedOnSchema.nullish(),
    lat: z.number().min(-90).max(90).nullish(),
    lon: z.number().min(-180).max(180).nullish(),
    notes: z.string().nullish(),
    /**
     * Les **préconisations** de l'agent — la section « TRAVAUX (ou améliorations) à réaliser » de la
     * fiche, qui remplit le bloc « Travaux à réaliser » du rapport client.
     *
     * ⚠️ **Du texte libre, et rien de plus** (Q7, 21/09/2026) : ni liste de travaux, ni délai, ni
     * code — l'entité « travaux » reste écartée, seule la place où l'écrire a été ajoutée. La
     * tablette y met ce que l'agent a gardé de `suggestRecommendations` (#345).
     *
     * `nullish` pour la raison de `notes` et de `performedOn` : le contrat est un `looseObject`, une
     * tablette d'avant ce champ transmet une fiche sans lui, et la refuser détruirait un relevé que
     * personne ne peut ressaisir (M21).
     */
    recommendations: z.string().nullish(),
};
/**
 * Le contenu d'une proposition **au relevé courant** — ce qu'une tablette à jour dépose, et ce
 * que la lecture rend une fois la montée de version appliquée (`readProposalPayload`).
 *
 * `looseObject` de bout en bout, pour la raison de M16 : une tablette peut porter une version
 * plus récente que l'API le temps d'un déploiement, et valider en `strict` reviendrait à effacer
 * une fiche que personne ne pourra ressaisir.
 */
export const currentInspectionProposalSchema = z.looseObject({
    ...proposalFields,
    survey: surveyV2Schema,
    surveyVersion: z.literal(SURVEY_VERSION),
});
/**
 * Le même contenu **avec un relevé v1** : ce qu'une tablette non encore mise à jour dépose, et
 * ce que porte tout contrôle transmis avant la v2. Il n'est jamais rendu tel quel — la lecture le
 * monte en v2 —, mais il doit continuer d'entrer, sans quoi une tournée en cours serait refusée
 * le jour du déploiement (M21).
 */
const legacyInspectionProposalSchema = z.looseObject({
    ...proposalFields,
    survey: surveyV1Schema,
    surveyVersion: z.literal(SURVEY_V1_VERSION),
});
/**
 * Ce que le dépôt accepte : **l'une ou l'autre version du relevé**, discriminées par
 * `surveyVersion`.
 *
 * ⚠️ Union discriminée et non « entier positif » : `survey` est **toujours** validé contre le
 * schéma de la version annoncée, donc accepter un autre numéro rangerait en base une version que
 * rien n'a vérifiée — et qu'un lecteur relirait un jour comme si c'était la sienne.
 */
export const inspectionProposalV1Schema = z.discriminatedUnion('surveyVersion', [
    legacyInspectionProposalSchema,
    currentInspectionProposalSchema,
]);
/**
 * La montée de version d'une proposition — le pendant d'`upgradeSurvey` sur l'enveloppe.
 *
 * ⚠️ Elle ne touche qu'au relevé : le reste de la charge utile (anomalies, pièces, signature) ne
 * dépend pas de la version du relevé, et le recopier champ à champ ferait perdre au premier oubli
 * ce que `looseObject` a justement gardé.
 *
 * ⚠️ `null` quand le relevé ne monte pas — voir `upgradeSurvey`, qui dit pourquoi ce n'est pas une
 * exception. Le type de retour est ce qui **oblige** l'appelant à traiter le cas : une fiche
 * illisible se journalise et se retire de la liste, elle n'emporte pas les autres.
 */
export function upgradeProposal(proposal) {
    if (proposal.surveyVersion === SURVEY_VERSION)
        return proposal;
    const survey = upgradeSurvey(proposal.survey);
    if (survey === null)
        return null;
    return { ...proposal, survey, surveyVersion: SURVEY_VERSION };
}
/** La version du contenu ci-dessus — c'est elle que `Proposal.payload_version` stocke. */
export const PROPOSAL_PAYLOAD_VERSION = 1;
/**
 * D4 — l'enveloppe de dépôt : ce que l'outbox de la tablette envoie, et ce que l'endpoint
 * d'ingestion range en « en attente de validation ». Le terrain **n'écrit pas** en base.
 *
 * `idempotencyKey` est portée par le contrôle et persistée en base (`UNIQUE (team_id,
 * idempotency_key)`), jamais dans un cache volatil : c'est ce qui fait survivre l'idempotence à
 * un redémarrage de l'API, et ce qui rend inoffensif le rejeu d'un envoi interrompu.
 *
 * `submittedAt` est l'instant où la tablette a clos la fiche, pas celui où l'API a reçu — d'où
 * l'exigence d'un décalage horaire explicite (M5).
 */
export const inspectionSubmissionSchema = z.looseObject({
    idempotencyKey: uuidV7Schema,
    inquiryId: uuidV7Schema,
    submittedAt: z.iso.datetime({ offset: true }),
    // `literal`, pas « entier positif » : `payload` est **toujours** validé contre le schéma v1,
    // donc un envoi annonçant `payloadVersion: 2` serait persisté avec un numéro que rien n'a
    // vérifié — et relu en v2 un jour, en silence. Union discriminée à la deuxième version.
    payloadVersion: z.literal(PROPOSAL_PAYLOAD_VERSION),
    payload: inspectionProposalV1Schema,
    /**
     * Présent sur un **contrôle libre** (#419, M41) : `inquiryId` est alors l'identifiant que la
     * tablette a tiré (M7), et aucune demande ne le porte encore. Le dépôt range la saisie sans
     * demande ; le bureau la rattachera. Ignoré quand la demande existe — rattachée à cet
     * identifiant, ou rejeu d'une seconde tablette : le dépôt suit alors le chemin ordinaire.
     */
    freeInspection: freeInspectionInputSchema.optional(),
});
//# sourceMappingURL=inspection-submission.js.map